import React, {useState, useEffect, useImperativeHandle, forwardRef, useRef, useMemo} from 'react'
import {
  SECTIONS,
  REACT_FLOW_MIME_TYPE,
  INITIAL_NODES,
  INITIAL_EDGES,
} from '../../../../../Utils/Constant/FlowConstants'
import './SidebarLeft.scss'
import {toAbsoluteUrl} from '../../../../../../_metronic/helpers'
import Select from 'react-select'
import {useNodes, useEdges, useReactFlow, type Node} from 'reactflow'
import {Formik} from 'formik'
import * as Yup from 'yup'
import {
  useCreateFlowMutation,
  useUpdateFlowMutation,
  useGetCreditRequiredMutation,
} from '../../../../../../services/cellcast.v2'
import {FlowStatus, DataNode, Flow} from '../FlowTypes'
import {toast} from 'react-toastify'
import {useNavigate} from 'react-router-dom'
import {RoutePaths} from '../../../../../routing/RoutePath'
import {useDispatch, useSelector} from 'react-redux'
import {clearFlowDraft, setFlowName} from '../../../../../Redux/FrontEnd/FlowDraftSlice'
import {useI18n} from '../../../../../Utils/_helpers/useI18n'
import {Modal, Button, Form as BSForm, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {getLayoutedElements} from '../FlowCanvas/FlowCanvas'
import {getAllUserDetails} from '../../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {IndustryOptions} from '../../../../AdminPages/AllCellCastUser/Constant/CellCastConstant'
import PurchasePopup, {getMinimumCredit} from '../../../../../Components/PurchasePopup'
import {useFlowDragDrop} from '../FlowCanvas/DragDropContext'

export type FormError = {
  name?: string
  nodes?: string
}

type NodeItem = {
  type: string
  icon: string
  color: string
  svg?: string
  bgColor?: string
}

type DraggableNodeProps = {
  item: NodeItem
  onDragStart: (event: React.DragEvent, nodeType: string) => void
  isVisible: boolean
  onSelect?: (nodeType: string) => void
}

export const DraggableNode: React.FC<DraggableNodeProps> = ({
  item,
  onDragStart,
  isVisible,
  onSelect,
}) => {
  const {t} = useI18n()
  const {addNodeBetweenEdges} = useFlowDragDrop()
  const pointerDragRef = useRef<{cleanup?: () => void} | null>(null)

  const handleSelect = () => {
    onSelect?.(item.type)
  }

  const startPointerDrag = (ev: React.PointerEvent) => {
    if (!addNodeBetweenEdges) return
    ev.preventDefault()
    ev.stopPropagation()

    const pointerId = ev.pointerId
    const startX = ev.clientX
    const startY = ev.clientY

    const sourceEl = (ev.currentTarget as HTMLElement).closest('.draggable-node') as
      | HTMLElement
      | null
    let ghost: HTMLElement | null = null

    let lastX = startX
    let lastY = startY
    let frameRequested = false
    const MIN_DRAG_DIST = 6

    const halos = Array.from(document.querySelectorAll<HTMLElement>('.flow-drop-halo'))
    let haloRects = halos.map((el) => ({el, rect: el.getBoundingClientRect()}))
    const refreshHaloRects = () => {
      haloRects = halos.map((el) => ({el, rect: el.getBoundingClientRect()}))
    }

    try {
      ;(ev.currentTarget as HTMLElement).setPointerCapture(pointerId)
    } catch {}

    // Helper to lazily create ghost only after real drag begins
    let ghostHalfW = 0
    let ghostHalfH = 0
    const ensureGhost = () => {
      if (ghost) return
      const rect = document.createElement('div')
      rect.className = 'drag-ghost'
      rect.style.transform = `translate(${startX}px, ${startY}px) translate(-50%, -50%)`

      const plus = document.createElement('div')
      plus.className = 'drag-ghost__plus'
      plus.textContent = '+'

      const label = document.createElement('div')
      label.className = 'drag-ghost__label'
      label.textContent = t(`FLOW.NODE.${item.type.toUpperCase()}`)

      rect.appendChild(plus)
      rect.appendChild(label)
      document.body.appendChild(rect)
      ghost = rect

      // Prepare for smooth movement
      ghost.style.left = '0px'
      ghost.style.top = '0px'
      ghost.style.willChange = 'transform'

      // Cache sizes
      ghostHalfW = sourceEl ? ghost.offsetWidth / 2 : 24
      ghostHalfH = sourceEl ? ghost.offsetHeight / 2 : 24

      // Disable text selection after we confirmed dragging
      prevUserSelect = document.body.style.userSelect
      document.body.style.userSelect = 'none'
    }

    const applyFrame = () => {
      frameRequested = false
      if (!ghost) return
      if (sourceEl) {
        ghost.style.transform = `translate3d(${lastX}px, ${lastY}px, 0) translate(-50%, -50%)`
      } else {
        ghost.style.transform = `translate3d(${lastX - 24}px, ${lastY - 24}px, 0)`
      }

      // Compute ghost rect analytically to avoid forced layout
      const ghostRect = {
        left: sourceEl ? lastX - ghostHalfW : lastX - 24,
        top: sourceEl ? lastY - ghostHalfH : lastY - 24,
        right: sourceEl ? lastX + ghostHalfW : lastX + 24,
        bottom: sourceEl ? lastY + ghostHalfH : lastY + 24,
      }

      let anyOverlap = false
      for (const {el, rect: r} of haloRects) {
        const overlap = !(
          ghostRect.right < r.left ||
          ghostRect.left > r.right ||
          ghostRect.bottom < r.top ||
          ghostRect.top > r.bottom
        )
        if (overlap) {
          el.classList.add('dragover')
          anyOverlap = true
        } else {
          el.classList.remove('dragover')
        }
      }
      if (!anyOverlap) {
        halos.forEach((el) => el.classList.remove('dragover'))
      }
    }

    let prevUserSelect = document.body.style.userSelect

    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      lastX = e.clientX
      lastY = e.clientY
      // Only create ghost if moved beyond threshold
      if (!ghost) {
        const dx = lastX - startX
        const dy = lastY - startY
        if (dx * dx + dy * dy >= MIN_DRAG_DIST * MIN_DRAG_DIST) {
          ensureGhost()
        }
      }
      if (!frameRequested) {
        frameRequested = true
        window.requestAnimationFrame(applyFrame)
      }
    }

    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      if (!ghost) {
        // It was a click, not a drag
        cleanup()
        return
      }
      // Use cached sizes and rects
      const ghostRect = {
        left: sourceEl ? lastX - ghostHalfW : lastX - 24,
        top: sourceEl ? lastY - ghostHalfH : lastY - 24,
        right: sourceEl ? lastX + ghostHalfW : lastX + 24,
        bottom: sourceEl ? lastY + ghostHalfH : lastY + 24,
      }
      let dropped = false
      for (const {el, rect: r} of haloRects) {
        const overlap = !(
          ghostRect.right < r.left ||
          ghostRect.left > r.right ||
          ghostRect.bottom < r.top ||
          ghostRect.top > r.bottom
        )
        if (overlap) {
          const source = el.getAttribute('data-source') || ''
          const target = el.getAttribute('data-target') || ''
          const posXAttr = el.getAttribute('data-pos-x')
          const posYAttr = el.getAttribute('data-pos-y')
          const x = posXAttr ? parseFloat(posXAttr) : 0
          const y = posYAttr ? parseFloat(posYAttr) : 0
          const type = (item.type || '').toLowerCase() as any
          addNodeBetweenEdges?.(type, source, target, {x, y})
          dropped = true
          break
        }
      }
      cleanup()
      if (!dropped) {
        // optional: feedback
      }
    }

    const cleanup = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('resize', refreshHaloRects)
      window.removeEventListener('scroll', refreshHaloRects, true)
      if (ghost) ghost.remove()
      document
        .querySelectorAll('.flow-drop-halo.dragover')
        .forEach((el) => el.classList.remove('dragover'))
      try {
        ;(ev.currentTarget as HTMLElement).releasePointerCapture(pointerId)
      } catch {}
      document.body.style.userSelect = prevUserSelect
      frameRequested = false
    }

    window.addEventListener('pointermove', onMove, {passive: true})
    window.addEventListener('pointerup', onUp, {once: true})
    window.addEventListener('resize', refreshHaloRects)
    window.addEventListener('scroll', refreshHaloRects, true)
    pointerDragRef.current = {cleanup}
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    startPointerDrag(e)
  }

  React.useEffect(() => {
    return () => {
      pointerDragRef.current?.cleanup?.()
    }
  }, [])

  return (
    <>
      {isVisible && (
        <div
          key={item.type}
          draggable
          onClick={handleSelect}
          onDragStart={(e) => onDragStart(e, item.type)}
          onPointerDown={handlePointerDown}
          className='p-4 cursor-pointer mb-2 bg-white border rounded text-center d-flex align-items-center justify-content-between gap-2 draggable-node channel-item'
          title={t(`FLOW.NODE.${item.type.toUpperCase()}_DESC`)}
        >
          <div className='d-flex align-items-center gap-2'>
            <div
              className='automation-icon-container'
              style={{
                backgroundColor: `${item.bgColor}`,
              }}
            >
              <img
                src={toAbsoluteUrl(item.svg || '')}
                alt={item.icon}
                className='automation-icon'
              />
            </div>
            {t(`FLOW.NODE.${item.type.toUpperCase()}`)}
          </div>
          <i
            className='bi bi-grip-vertical'
            draggable
            onDragStart={(e) => onDragStart(e, item.type)}
            onPointerDown={handlePointerDown}
            style={{cursor: 'grab', fontSize: '1.5rem'}}
            title={t(`FLOW.NODE.${item.type.toUpperCase()}_DESC`)}
          />
        </div>
      )}
    </>
  )
}

export type SidebarLeftHandle = {
  submitForm: () => void
  isLoading: boolean
}

const SidebarLeft = forwardRef<
  SidebarLeftHandle,
  {
    payload?: Flow
    isEdit: boolean
    onFlowDataChange?: (name: string, status: FlowStatus) => void
    formError?: FormError
    onSubmit?: (values: {flowName: string; status: FlowStatus}) => void
    onFlowNameChange?: (name: string) => void
    replyWindowMinutes: number
  }
>(({payload, isEdit, onFlowDataChange, formError, onSubmit, onFlowNameChange, replyWindowMinutes}, ref) => {
  const navigate = useNavigate()
  const {t} = useI18n()
  const [statusState, setStatus] = useState(payload?.status || FlowStatus.ACTIVE)
  const nodes = useNodes() || []
  const edges = useEdges() || []
  const dispatch = useDispatch()
  const reduxFlowName = useSelector((state: any) => state.flowDraft?.flowName)
  const [purchasePopupModal, setPurchasePopupModal] = useState<any>(false)
  const [purchaseModelData, setPurchaseModelData] = useState({
    type: 'sms',
    purchaseCredit: 0,
    minimum: 0,
    purpose: 'create the flow',
  })
  const flowName = useMemo(() => {
    if (isEdit) {
      return payload?.name || ''
    }
    return reduxFlowName || ''
  }, [isEdit, payload?.name, reduxFlowName])

  const {setNodes, setEdges} = useReactFlow()
  const [showResetModal, setShowResetModal] = useState(false)

  const [createFlow, {isLoading}] = useCreateFlowMutation()
  const [updateFlow, {isLoading: isUpdating}] = useUpdateFlowMutation()
  const [getCreditRequired, {isLoading: isCheckingCredits}] = useGetCreditRequiredMutation()

  const purchaseModal = useMemo(
    () => (
      <PurchasePopup
        show={purchasePopupModal}
        purchaseModelData={purchaseModelData}
        handleClose={() => {
          setPurchasePopupModal(false)
        }}
        purpose={purchaseModelData.purpose}
      />
    ),
    [purchasePopupModal, purchaseModelData]
  )

  const validationSchema = Yup.object({
    flowName: Yup.string().required('Flow name is required'),
  })

  const userDetails = useSelector(getAllUserDetails)
  const userRole = userDetails?.role
  const isAdmin = ['superAdmin', 'accountManager', 'subAdmin', 'sales', 'affiliate'].includes(
    userRole
  )

  useEffect(() => {
    if (onFlowDataChange) {
      onFlowDataChange(flowName, statusState)
    }
  }, [onFlowDataChange, flowName, statusState])

  const handleFormikChange = (_e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {}

  const handleDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData(REACT_FLOW_MIME_TYPE, nodeType)
  }

  const handleResetClick = () => {
    setShowResetModal(true)
  }

  const handleResetConfirm = () => {
    const {nodes: initialNodes, edges: initialEdges} = getLayoutedElements(
      [...INITIAL_NODES],
      [...INITIAL_EDGES]
    )

    setNodes(initialNodes)
    setEdges(initialEdges)

    dispatch(clearFlowDraft())

    dispatch(setFlowName(''))

    setShowResetModal(false)

    toast.success('Flow has been reset to initial state')
  }

  const handleResetCancel = () => {
    setShowResetModal(false)
  }

  const formikRef = useRef<any>(null)

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      formikRef.current?.submitForm()
    },
    isLoading: isLoading || isUpdating || isCheckingCredits,
  }))

  return (
    <>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          flowName,
          status: statusState,
          scope: payload?.scope,
          industry: payload?.industry,
          affiliate: payload?.affiliate,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, {setSubmitting, resetForm}) => {
          if (onSubmit) onSubmit(values)

          try {
            const creditCheck = await getCreditRequired({
              id: payload?._id as string,
              nodes: nodes,
            }).unwrap()

            if (!creditCheck.data.status) {
              setPurchaseModelData({
                type: 'sms',
                purchaseCredit: getMinimumCredit(creditCheck.data?.requiredCredits),
                minimum: creditCheck.data?.requiredCredits,
                purpose: isEdit
                  ? 'update the flow (SMS required)'
                  : 'create the flow (SMS required)',
              })
              setPurchasePopupModal(true)
              setSubmitting(false)
              return
            }
          } catch (error) {
            console.error('Credit check failed:', error)
          }

          let result
          if (isEdit) {
            const baseSettings =
              (payload?.settings as Record<string, unknown> | undefined) ?? {}
            result = await updateFlow({
              id: payload?._id as string,
              payload: {
                name: values.flowName,
                scope: values.scope ?? 'GLOBAL',
                industry: values.industry,
                affiliate: values.affiliate,
                nodes: nodes as Node<DataNode<any>>[],
                edges,
                status: values.status as FlowStatus,
                settings: {
                  ...baseSettings,
                  customerWaitingReplyWindowMinutes: replyWindowMinutes,
                },
              },
            })
          } else {
            result = await createFlow({
              name: values.flowName,
              scope: values.scope ?? 'GLOBAL',
              industry: values.industry,
              affiliate: values.affiliate,
              nodes: nodes as Node<DataNode<any>>[],
              edges,
              status: values.status as FlowStatus,
              settings: {
                customerWaitingReplyWindowMinutes: replyWindowMinutes,
              },
            })
          }
          try {
            if ('data' in result && result.data) {
              toast.success(isEdit ? 'Flow updated successfully!' : 'Flow created successfully!')
              resetForm()
              if (!isEdit) {
                dispatch(clearFlowDraft())
              }
              isAdmin ? navigate('/templates/flows') : navigate(RoutePaths.automation)
            } else if ('error' in result && result.error) {
              const errorMsg =
                (
                  result.error as {
                    data: {
                      message: string
                    }
                  }
                )?.data?.message ||
                (
                  result.error as {
                    message: string
                  }
                )?.message ||
                'Failed to create flow'
              toast.error(errorMsg)
            }
          } catch (err: any) {
            toast.error(err?.message || 'Failed to create flow')
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          errors,
          touched,
          isSubmitting,
        }) => (
          <>
            {purchaseModal}
            <form id='flow-form' onSubmit={handleSubmit} className='p-1'>
              <div className='mb-3  pt-3'>
                <BSForm.Group>
                  <BSForm.Label
                    htmlFor='flowName'
                    className='d-flex required  form-label fw-bold'
                    title={t('FLOW.SIDEBAR.FLOW_NAME_DESC')}
                  >
                    <h5>{t('FLOW.SIDEBAR.FLOW_NAME')}</h5>
                  </BSForm.Label>
                </BSForm.Group>
                <input
                  id='flowName'
                  name='flowName'
                  type='text'
                  className={`form-control${
                    (errors.flowName && touched.flowName) || formError?.name ? ' is-invalid' : ''
                  }`}
                  placeholder={t('FLOW.SIDEBAR.FLOW_NAME_PLACEHOLDER')}
                  value={values.flowName}
                  onChange={(e) => {
                    handleChange(e)
                    handleFormikChange(e)
                    onFlowNameChange?.(e.target.value)
                  }}
                  onBlur={handleBlur}
                  maxLength={64}
                />
                {errors.flowName && touched.flowName ? (
                  <div className='invalid-feedback d-block'>{errors.flowName}</div>
                ) : formError?.name ? (
                  <div className='invalid-feedback d-block'>{formError.name}</div>
                ) : null}

                <div className='border-bottom my-3'></div>
              </div>

              {isAdmin ? (
                <>
                  <div className='mb-5 gap-2'>
                    <label className='form-label'>{t('FLOW.SIDEBAR.FLOW_SCOPE')}</label>
                    <div className='d-flex gap-4'>
                      <div className='form-check form-check-custom form-check-solid mb-2'>
                        <input
                          className='form-check-input'
                          type='radio'
                          value='B-CARD'
                          id='b-card'
                          name='b-card'
                          checked={values?.scope === 'B-CARD'}
                          onChange={() => {
                            handleChange({
                              target: {
                                name: 'scope',
                                value: 'B-CARD',
                              },
                            } as React.ChangeEvent<any>)
                          }}
                        />
                        <label className='form-check-label fw-bold' htmlFor='b-card'>
                          B-Card
                        </label>
                      </div>

                      <div className='form-check form-check-custom form-check-solid mb-2'>
                        <input
                          className='form-check-input'
                          type='radio'
                          value='GLOBAL'
                          id='global'
                          name='global'
                          checked={values?.scope === 'GLOBAL'}
                          onChange={() => {
                            handleChange({
                              target: {
                                name: 'scope',
                                value: 'GLOBAL',
                              },
                            } as React.ChangeEvent<any>)
                          }}
                        />
                        <label className='form-check-label fw-bold' htmlFor='global'>
                          All Account
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className='mb-7'>
                    <label className='form-label'>{t('FLOW.SIDEBAR.FLOW_INDUSTRY')}</label>
                    <Select<{value: string; label: string}>
                      value={
                        IndustryOptions.find((option) => option.value === values?.industry) || null
                      }
                      isSearchable={false}
                      name='industry'
                      options={IndustryOptions}
                      onChange={(selected) => {
                        handleChange({
                          target: {
                            name: 'industry',
                            value: selected ? selected.value : '',
                          },
                        } as React.ChangeEvent<any>)
                      }}
                    />
                  </div>
                  <div className='mb-7'>
                    <label className='form-label'>{t('FLOW.SIDEBAR.FLOW_AFFILIATE')}</label>
                    <input
                      type='text'
                      className='form-control form-control-solid border-0 '
                      placeholder='Affiliate Code'
                      name='affiliate'
                      value={values?.affiliate}
                      onChange={(e) => {
                        handleChange(e)
                      }}
                    />
                  </div>
                </>
              ) : null}

              {SECTIONS.map(({title, items, moreInfo}) => (
                <React.Fragment key={title}>
                  <div className='d-flex flex-column gap-1 p-1'>
                    <h5 className=' d-flex align-items-center gap-1' title={t(moreInfo || '')}>
                      {t(title)}
                      <i
                        className='bi bi-info-circle ms-1'
                        style={{fontSize: '1rem', color: '#A0A0A0', cursor: 'pointer'}}
                        title={t(moreInfo || '')}
                        tabIndex={0}
                        aria-label='More info'
                      />
                    </h5>
                    {items.map((item) => (
                      <DraggableNode
                        key={item.type}
                        item={item}
                        onDragStart={handleDragStart}
                        isVisible={item.isVisible}
                      />
                    ))}
                  </div>
                </React.Fragment>
              ))}

              <div className='border-top border-gray-300 p-1 mt-3'>
                <label className='form-label fw-bold' title={t('FLOW.SIDEBAR.STATUS_DESC')}>
                  Status
                </label>
                <Select<{value: FlowStatus; label: string}>
                  className='common-select z-1000'
                  isSearchable={false}
                  options={[
                    {label: 'Active', value: FlowStatus.ACTIVE},
                    {label: 'Inactive', value: FlowStatus.INACTIVE},
                  ]}
                  value={
                    values.status === FlowStatus.ACTIVE
                      ? {label: 'Active', value: FlowStatus.ACTIVE}
                      : {label: 'Inactive', value: FlowStatus.INACTIVE}
                  }
                  onChange={(opt) => {
                    const newVal = opt?.value || FlowStatus.ACTIVE
                    setFieldValue('status', newVal)
                    setStatus(newVal as FlowStatus)
                  }}
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
                  closeMenuOnScroll={true}
                  menuShouldBlockScroll={false}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: 'var(--bs-gray-200) !important',
                      borderColor: base.borderColor,
                      boxShadow: base.boxShadow,
                      minHeight: 32,
                    }),
                    valueContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
                    indicatorsContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
                  }}
                />
              </div>

              {formError?.nodes && (
                <div className='invalid-feedback d-block'>{formError.nodes}</div>
              )}

              <div className='d-flex justify-content-between align-items-center mt-3 pt-3 gap-2'>
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip-reset'>{t('FLOW.SIDEBAR.RESET_FLOW_DESC')}</Tooltip>
                  }
                >
                  <button
                    className='btn btn-outline btn-outline-danger px-4 py-3 d-flex align-items-center gap-2'
                    type='button'
                    disabled={isLoading || isSubmitting || isUpdating || isCheckingCredits}
                    onClick={handleResetClick}
                  >
                    <i className='fas fa-undo'></i>
                    {t('FLOW.SIDEBAR.RESET')}
                  </button>
                </OverlayTrigger>

                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip-back'>{t('FLOW.SIDEBAR.BACK_TO_LIST_DESC')}</Tooltip>
                  }
                >
                  <button
                    className='btn btn-outline btn-outline-secondary px-4 py-3 d-flex align-items-center gap-2'
                    type='button'
                    disabled={isLoading || isSubmitting || isUpdating || isCheckingCredits}
                    onClick={() => navigate('/automation')}
                  >
                    <i className='fas fa-arrow-left'></i>
                    {t('FLOW.SIDEBAR.BACK_TO_LIST')}
                  </button>
                </OverlayTrigger>
              </div>
            </form>
          </>
        )}
      </Formik>

      <Modal show={showResetModal} onHide={handleResetCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title className='w-100 text-start d-flex align-items-center'>
            <i className='fas fa-exclamation-triangle text-warning me-2'></i>
            {t('FLOW.SIDEBAR.RESET_FLOW')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('FLOW.SIDEBAR.CONFIRMATION')}</p>
          <p className='text-muted small mb-0'>{t('FLOW.SIDEBAR.RESET_FLOW_CONFIRMATION')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleResetCancel}>
            {t('ACTIONS.CANCEL')}
          </Button>
          <Button variant='danger' onClick={handleResetConfirm}>
            <i className='fas fa-undo me-2'></i>
            {t('FLOW.SIDEBAR.RESET_FLOW')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
})

export default SidebarLeft
